import {
  AssetField,
  AssetSelect,
  Button,
  Heading,
  InfoText,
  InputLikeButton,
  TextFieldRef,
} from '@altify/components';
import { formatAssetAmount, useContent } from '@altify/utils';
import { useDismissableModal } from '@hooks/useOneTimeModal';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import CurrencyIcon from 'app/_authenticated/trade/components/CurrencyIcon/CurrencyIcon';
import {
  useFunctionalCurrency,
  useProduct,
  useProductWithHoldings,
} from 'hooks/products/useProducts';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useCurrentRewardTier } from 'hooks/useCurrentRewardTier';
import { useEffect, useRef, useState } from 'react';
import { useCreateRecurringBuyMutation } from 'store/slices/recurringBuy-api';
import { useGetQuoteDataQuery } from 'store/slices/trade-api';
import { capitalize } from 'utils/casing';
import { z } from 'zod';
import BundleRebalanceTermsModal from '../components/BundleRebalanceTermsModal/BundleRebalanceTermsModal';
import { useBundleRebalanceVisibility } from '../components/BundleRebalanceTermsModal/useBundleRebalanceVisibility';
import CryptoDisclosureModal from '../components/CryptoDisclosureModal/CryptoDisclosureModal';
import SelectionCurrencyModal from '../components/SelectionCurrencyModal/SelectionCurrencyModal';
import { tradeSharedContent } from '../tradeShared.content';
import { CalendarIcon } from './components/CalendarIcon';
import RecurringBuyConfirmModal from './components/RecurringBuyConfirmModal/RecurringBuyConfirmModal';
import RecurringBuyScheduleModal from './components/RecurringBuyScheduleModal/RecurringBuyScheduleModal';
import { RecurringBuySubmitModal } from './components/RecurringBuySubmitModal/RecurringBuySubmitModal';
import { RecurringBuySchedule } from './models/RecurringBuySchedule';
import { recurringBuySharedContent } from './recurringBuyShared.content';
import { content } from './route.content';
import {
  EVENTS,
  trackEvent,
  tradeEventFields,
  TRANSACTION_TYPE,
} from 'utils/analytics';
import { ActiveProductDtoTypeEnum } from 'services/generated';
import StockDisclosureModal from '../components/StockDisclosureModal/StockDisclosureModal';
import { formatCurrency } from 'utils/currencies';

export const Route = createFileRoute('/_authenticated/trade/recurring-buy')({
  component: RecurringBuyScreen,
  validateSearch: zodValidator(
    z.object({
      productCode: z.string().optional(),
    }),
  ),
});

function RecurringBuyScreen() {
  const { productCode: toProductCode } = Route.useSearch();
  const router = useRouter();
  const localizedContent = useContent(
    content,
    recurringBuySharedContent,
    tradeSharedContent,
  );
  const dispatch = useAppDispatch();
  const fromAssetFieldRef = useRef<TextFieldRef>(null);
  const [confirmModalIsOpen, setConfirmModalIsOpen] = useState(false);
  const [submitOrderIsOpen, setSubmitOrderIsOpen] = useState(false);
  const [scheduleModalIsOpen, setScheduleModalIsOpen] = useState(false);
  const [fromAmount, setFromAmount] = useState<number | null>(null);
  const { functionalCurrency } = useFunctionalCurrency();
  const { currentTier } = useCurrentRewardTier();
  const fromProductCode = functionalCurrency?.code!;

  const [recurringBuySchedule, setRecurringBuySchedule] =
    useState<RecurringBuySchedule | null>(null);

  const [createRecurringBuy, createRecurringBuyResult] =
    useCreateRecurringBuyMutation();

  const {
    showModal: cryptoDisclosureModalIsOpen,
    dismissModal: dismissCryptoDisclosureModal,
  } = useDismissableModal('cryptoDisclosure');
  const {
    showModal: stocksDisclosureModalIsOpen,
    dismissModal: dismissStocksDisclosureModal,
  } = useDismissableModal('stocksDisclosure');
  const { data: quoteData, isFetching: quoteDataIsFetching } =
    useGetQuoteDataQuery(
      {
        fromProductCode: fromProductCode?.toUpperCase()!,
        toProductCode: toProductCode?.toUpperCase()!,
        type: 'buy',
      },
      { skip: !fromProductCode || !toProductCode },
    );

  const lastKnownPrice = 1 / quoteData?.approximateFromProductToToProductRate!;

  const { product: fromProduct } = useProductWithHoldings(fromProductCode!);
  const { product: toProduct } = useProduct(toProductCode!);

  const fromProductAvailableAmount = fromProduct?.availableAmount ?? 0;
  const title = localizedContent.title(toProduct?.code, toProduct?.name);

  const bundleRebalanceVisibility = useBundleRebalanceVisibility(
    toProductCode ?? '',
  );

  const [bundleRebalanceIsOpen, setBundleRebalanceIsOpen] = useState(
    bundleRebalanceVisibility,
  );
  const [selectionCurrencyIsOpen, setSelectionCurrencyIsOpen] =
    useState(!toProductCode);

  useEffect(() => {
    if (fromProductCode && toProductCode) {
      fromAssetFieldRef.current?.focus();
    }
  }, [fromProductCode, toProductCode]);

  useEffect(() => {
    if (createRecurringBuyResult?.isSuccess) {
      // TRACK RECURRING BUY CREATED
      trackEvent(EVENTS.RECURRING_BUY_CREATED, {
        [tradeEventFields.amountFrom]: fromAmount!,
        [tradeEventFields.fromCurrencyCode]: fromProductCode,
        [tradeEventFields.toAmountCrypto]:
          createRecurringBuyResult?.data?.amountPurchased,
        [tradeEventFields.toCurrencyCode]: toProductCode!,
        [tradeEventFields.feeCurrency]: fromProductCode,
        [tradeEventFields.tradeType]: TRANSACTION_TYPE.RECURRING_BUY,
        [tradeEventFields.buyNow]: recurringBuySchedule!.buyNow,
        [tradeEventFields.schedule]: recurringBuySchedule!.interval,
      });
    }
  }, [createRecurringBuyResult]);

  const approximateFiatValue =
    quoteData == null || fromAmount == null
      ? null
      : fromAmount / quoteData.approximateFunctionalToFromProductRate;

  const minimumFromValue =
    quoteData == null
      ? null
      : quoteData.approximateFunctionalToFromProductRate *
        quoteData?.minimumAmount;

  const maximumFromValue =
    quoteData == null || quoteData.maximumAmount == null
      ? null
      : quoteData.approximateFunctionalToFromProductRate *
        quoteData.maximumAmount;
  const fromInfoTexts = {
    default: `${formatAssetAmount(fromProductAvailableAmount ?? null, fromProduct?.code!, { showSpacer: true })} ${
      localizedContent.available
    }`,
    exceedsBalance: localizedContent.exceedsBalanceOf(
      formatCurrency(fromProductAvailableAmount ?? null, fromProduct?.code!),
    ),
    belowMinimum: localizedContent.enterAMinimumOf(
      formatCurrency(minimumFromValue!, fromProduct?.code!),
    ),
    aboveMaximum: localizedContent.enterAMaximumOf(
      formatCurrency(maximumFromValue!, fromProduct?.code!),
    ),
  };

  const exceedsBalanceError =
    fromAmount! > fromProductAvailableAmount! && recurringBuySchedule?.buyNow;
  const belowMinimumError =
    fromAmount != null &&
    approximateFiatValue! < quoteData?.minimumAmount! &&
    !quoteDataIsFetching;

  const aboveMaximumError =
    fromAmount && quoteData && quoteData.maximumAmount && approximateFiatValue
      ? approximateFiatValue > quoteData.maximumAmount
      : false;

  const fromHasError =
    fromAmount != null &&
    (exceedsBalanceError || belowMinimumError || aboveMaximumError) &&
    !quoteDataIsFetching;

  const bothAssetsSelected = fromProductCode && toProductCode;
  const formComplete =
    fromProductCode &&
    toProductCode &&
    fromAmount &&
    !fromHasError &&
    recurringBuySchedule != null;

  const infoText = exceedsBalanceError
    ? fromInfoTexts.exceedsBalance
    : belowMinimumError
      ? fromInfoTexts.belowMinimum
      : aboveMaximumError
        ? fromInfoTexts.aboveMaximum
        : fromInfoTexts.default;

  async function handleCreateRecurringBuy() {
    await createRecurringBuy({
      productCode: toProductCode!,
      amount: fromAmount!,
      executeImmediately: recurringBuySchedule!.buyNow,
      interval: capitalize(recurringBuySchedule!.interval),
      dayOfMonth:
        recurringBuySchedule!.interval == 'monthly'
          ? recurringBuySchedule?.dayOfMonth
          : undefined,
      dayOfWeek:
        recurringBuySchedule!.interval == 'weekly'
          ? recurringBuySchedule!.dayOfWeek
          : undefined,
    });
  }
  return (
    <>
      <div className="items-center px-4 flex flex-col flex-1 w-full">
        <div className="w-full items-center flex-1">
          <div className="flex flex-col items-center mb-10">
            {toProductCode && (
              <Heading level={4} className="mb-3 mt-5">
                {title}
              </Heading>
            )}
            {bothAssetsSelected && quoteData && (
              <InfoText
                title={` ${localizedContent.lastPrice}: `}
                text={formatAssetAmount(lastKnownPrice, fromProductCode!, {
                  showSpacer: true,
                })}
                hasError={false}
              />
            )}
          </div>
          <AssetSelect
            showDropdownIcon={true}
            iconComponent={CurrencyIcon}
            iconProps={{
              code: toProductCode!,
              size: 25,
              style: { marginRight: 5 },
            }}
            placeholder={localizedContent.select}
            selectedAssetCode={toProductCode?.toUpperCase() ?? undefined}
            onPress={() => {
              setSelectionCurrencyIsOpen(true);
            }}
            title={localizedContent.buy}
            className="w-full"
          />
          <AssetField
            ref={fromAssetFieldRef}
            assetIconComponent={CurrencyIcon}
            assetIconProps={{
              code: fromProductCode!,
              size: 25,
              style: { marginRight: 5 },
            }}
            assetCode={fromProductCode?.toUpperCase() ?? undefined}
            assetPlaceholder={localizedContent.select}
            amountReadonly={!fromProductCode}
            amount={fromAmount ?? null}
            onValueChanged={setFromAmount}
            hasError={fromHasError}
            showPlusMinusButtons={false}
            title={localizedContent.using}
            onPress={() => {
              setSelectionCurrencyIsOpen(true);
            }}
            assetSelectable={false}
            className="mt-8"
            infoText={infoText}
          />
          <InputLikeButton
            name={localizedContent.recurring}
            text={
              recurringBuySchedule != null
                ? localizedContent.recurringSchedule(recurringBuySchedule)
                : undefined
            }
            onPress={() => {
              setScheduleModalIsOpen(true);
            }}
            isFocused={scheduleModalIsOpen}
            iconComponent={CalendarIcon}
            placeholder={localizedContent.setSchedule}
            className="mt-4"
          />
        </div>
        <div className="w-full py-6">
          <Button
            size="large"
            color="primary"
            disabled={!formComplete}
            onPress={() => {
              setConfirmModalIsOpen(true);
            }}
            text={localizedContent.reviewBuy}
            className="w-full"
          />
        </div>
      </div>
      {selectionCurrencyIsOpen && (
        <SelectionCurrencyModal
          isOpen={selectionCurrencyIsOpen && !bundleRebalanceIsOpen}
          onClose={() => setSelectionCurrencyIsOpen(false)}
          fromProductCode={fromProductCode}
          toProductCode={toProductCode ?? null}
          onToProductCodeSelected={newToAsset => {
            if (newToAsset != toProductCode) {
              router.navigate({
                to: '/trade/recurring-buy',
                search: { productCode: newToAsset },
              });
            }
          }}
          side="to"
          type={'buy'}
        />
      )}
      {confirmModalIsOpen && (
        <RecurringBuyConfirmModal
          isOpen={confirmModalIsOpen}
          schedule={recurringBuySchedule!}
          onClose={() => {
            setConfirmModalIsOpen(false);
          }}
          onSubmit={() => {
            setConfirmModalIsOpen(false);
            setSubmitOrderIsOpen(true);
            handleCreateRecurringBuy();
          }}
          fromProductCode={fromProductCode!}
          toProductCode={toProductCode!}
          fromAmount={fromAmount!}
          rewardTierName={currentTier?.tierName!}
          rewardTierCode={currentTier?.tierId!}
        />
      )}
      {bundleRebalanceIsOpen && (
        <BundleRebalanceTermsModal
          isOpen={bundleRebalanceIsOpen}
          handleClose={dismissed => {
            //we need to set also the currency selection close otherwise it remains open when navigating back
            setSelectionCurrencyIsOpen(false);
            setBundleRebalanceIsOpen(false);
            if (dismissed) history.back();
          }}
        />
      )}
      {submitOrderIsOpen && (
        <RecurringBuySubmitModal
          schedule={recurringBuySchedule!}
          onClose={() => {
            setSubmitOrderIsOpen(false);
          }}
          tradeComplete={createRecurringBuyResult.isSuccess}
          tradeError={createRecurringBuyResult.isError}
          fromAmount={fromAmount!}
          toAmount={
            createRecurringBuyResult?.data?.amountPurchased ?? undefined
          }
          toProductCode={toProductCode!}
          fromProductCode={fromProductCode!}
          onCancelTrade={() => {
            setSubmitOrderIsOpen(false);
            history.back();
          }}
          onRetryTrade={() => {
            handleCreateRecurringBuy();
          }}
        />
      )}
      {scheduleModalIsOpen && (
        <RecurringBuyScheduleModal
          isOpen={scheduleModalIsOpen}
          schedule={recurringBuySchedule ?? undefined}
          onClose={() => {
            setScheduleModalIsOpen(false);
          }}
          onSubmit={setRecurringBuySchedule}
        />
      )}
      {toProduct?.type !== ActiveProductDtoTypeEnum.Stock &&
        cryptoDisclosureModalIsOpen && (
          <CryptoDisclosureModal
            isOpen={cryptoDisclosureModalIsOpen}
            onClose={dontShowAgain =>
              dismissCryptoDisclosureModal(dontShowAgain)
            }
          />
        )}
      {toProduct?.type === ActiveProductDtoTypeEnum.Stock &&
        stocksDisclosureModalIsOpen && (
          <StockDisclosureModal
            isOpen={stocksDisclosureModalIsOpen}
            onClose={dontShowAgain =>
              dismissStocksDisclosureModal(dontShowAgain)
            }
          />
        )}
    </>
  );
}
export default RecurringBuyScreen;
